import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowRight, Boxes, Building2, PackageX, TrendingUp, Users, Warehouse,
} from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { P } from '@stockpilot/shared';
import { useAppSelector } from '@/app/hooks';
import { useAccess, useFormat } from '@/hooks/useAccess';
import {
  useAuditLogsQuery, useMembersQuery, useStockQuery, useStockValuationQuery,
} from '@/api/endpoints';
import { formatDateTime, formatMoney, formatNumber, formatQuantity } from '@/lib/format';
import {
  Badge, Card, CardBody, CardHeader, EmptyState, LoadingState, PageHeader, Spinner,
  Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';

/**
 * Every figure on this page comes from the API. Nothing is estimated, and a
 * metric the user lacks permission to see is simply not requested.
 */
export function DashboardPage() {
  const context = useAppSelector((state) => state.tenant.context);
  const format = useFormat();
  const { can } = useAccess();

  const canSeeCost = can(P.STOCK_VIEW, P.PRODUCT_COST_VIEW);

  const valuation = useStockValuationQuery(undefined, { skip: !canSeeCost });
  const lowStock = useStockQuery({ lowStockOnly: true, limit: 5 }, { skip: !can(P.STOCK_VIEW) });
  const outOfStock = useStockQuery({ outOfStockOnly: true, limit: 1 }, { skip: !can(P.STOCK_VIEW) });
  const allStock = useStockQuery({ limit: 1 }, { skip: !can(P.STOCK_VIEW) });
  const team = useMembersQuery({ limit: 1 }, { skip: !can(P.USER_VIEW) });
  const audit = useAuditLogsQuery({ limit: 6 }, { skip: !can(P.AUDIT_VIEW) });

  const warehouseChart =
    valuation.data?.byWarehouse.map((entry) => ({
      name: entry.warehouseName,
      value: Number.parseFloat(entry.totalValue),
      skus: entry.skuCount,
    })) ?? [];

  return (
    <>
      <PageHeader
        title={`Good day${context?.organization.name ? `, ${context.organization.name}` : ''}`}
        description="A live view of your inventory position and recent activity."
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Can permission={[P.STOCK_VIEW, P.PRODUCT_COST_VIEW]}>
          <StatCard
            icon={TrendingUp}
            label="Inventory value"
            value={valuation.isLoading ? null : formatMoney(valuation.data?.totalValue, format)}
            hint="At moving-average cost"
            tone="brand"
          />
        </Can>

        <Can permission={P.STOCK_VIEW}>
          <StatCard
            icon={Boxes}
            label="Stock records"
            value={allStock.isLoading ? null : formatNumber(allStock.data?.meta.total ?? 0, format.locale)}
            hint="Product / warehouse balances"
          />
          <StatCard
            icon={AlertTriangle}
            label="At or below reorder level"
            value={lowStock.isLoading ? null : formatNumber(lowStock.data?.meta.total ?? 0, format.locale)}
            hint="Needs replenishment"
            tone={(lowStock.data?.meta.total ?? 0) > 0 ? 'warning' : 'neutral'}
            to="/app/inventory/stock?lowStockOnly=true"
          />
          <StatCard
            icon={PackageX}
            label="Out of stock"
            value={outOfStock.isLoading ? null : formatNumber(outOfStock.data?.meta.total ?? 0, format.locale)}
            hint="Zero or negative balance"
            tone={(outOfStock.data?.meta.total ?? 0) > 0 ? 'danger' : 'neutral'}
            to="/app/inventory/stock?outOfStockOnly=true"
          />
        </Can>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <Can permission={[P.STOCK_VIEW, P.PRODUCT_COST_VIEW]}>
            <Card>
              <CardHeader
                title="Inventory value by warehouse"
                description="Where your working capital is sitting right now"
              />
              <CardBody>
                {valuation.isLoading ? (
                  <LoadingState />
                ) : warehouseChart.length === 0 ? (
                  <EmptyState
                    icon={Warehouse}
                    title="No stock recorded yet"
                    description="Add products and post opening balances to see your inventory value here."
                  />
                ) : (
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={warehouseChart} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.1} vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="currentColor" opacity={0.6} />
                        <YAxis tick={{ fontSize: 12 }} stroke="currentColor" opacity={0.6} width={72} />
                        <Tooltip
                          formatter={(value: number) => formatMoney(value, format)}
                          contentStyle={{
                            background: 'var(--surface-raised)',
                            border: '1px solid var(--border-subtle)',
                            borderRadius: 8,
                            fontSize: 13,
                          }}
                        />
                        <Bar dataKey="value" fill="var(--color-brand-500)" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardBody>
            </Card>
          </Can>

          <Can permission={P.STOCK_VIEW}>
            <Card>
              <CardHeader
                title="Needs attention"
                description="Items at or below their reorder level"
                action={
                  <Link
                    to="/app/inventory/stock?lowStockOnly=true"
                    className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline"
                  >
                    View all <ArrowRight className="size-3.5" aria-hidden />
                  </Link>
                }
              />
              {lowStock.isLoading ? (
                <LoadingState />
              ) : (lowStock.data?.items.length ?? 0) === 0 ? (
                <EmptyState
                  icon={Boxes}
                  title="Nothing is running low"
                  description="Every tracked item is above its reorder level."
                />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>Product</Th>
                      <Th>Warehouse</Th>
                      <Th numeric>On hand</Th>
                      <Th numeric>Reorder at</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowStock.data?.items.map((row) => (
                      <tr key={row.id}>
                        <Td>
                          <p className="font-medium">{row.product.name}</p>
                          <p className="text-xs text-muted">{row.variant.sku}</p>
                        </Td>
                        <Td className="text-secondary">{row.warehouse.name}</Td>
                        <Td numeric>
                          <Badge tone={row.isOutOfStock ? 'danger' : 'warning'}>
                            {formatQuantity(row.quantity, format)} {row.product.baseUnit.symbol}
                          </Badge>
                        </Td>
                        <Td numeric className="text-secondary">
                          {formatQuantity(row.reorderLevel, format)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </Can>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Your business" />
            <CardBody className="space-y-3">
              <SummaryRow
                icon={Building2}
                label="Branches"
                value={formatNumber(context?.branches.length ?? 0, format.locale)}
              />
              <SummaryRow
                icon={Warehouse}
                label="Warehouses"
                value={formatNumber(context?.warehouses.length ?? 0, format.locale)}
              />
              <Can permission={P.USER_VIEW}>
                <SummaryRow
                  icon={Users}
                  label="Team members"
                  value={team.isLoading ? '…' : formatNumber(team.data?.meta.total ?? 0, format.locale)}
                />
              </Can>
              {context?.subscription && (
                <div className="border-t border-subtle pt-3">
                  <p className="text-sm text-secondary">Plan</p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="font-medium">{context.subscription.planName}</span>
                    <Badge tone={context.subscription.status === 'TRIALING' ? 'info' : 'success'}>
                      {context.subscription.status.toLowerCase()}
                    </Badge>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          <Can permission={P.AUDIT_VIEW}>
            <Card>
              <CardHeader
                title="Recent activity"
                action={
                  <Link to="/app/admin/audit" className="text-sm font-medium text-brand-600 hover:underline">
                    Audit log
                  </Link>
                }
              />
              <CardBody className="space-y-3">
                {audit.isLoading ? (
                  <Spinner />
                ) : (audit.data?.items.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted">No activity recorded yet.</p>
                ) : (
                  audit.data?.items.map((entry) => (
                    <div key={entry.id} className="text-sm">
                      <p>
                        <span className="font-medium">
                          {entry.user ? `${entry.user.firstName} ${entry.user.lastName}` : 'System'}
                        </span>{' '}
                        <span className="text-secondary">
                          {entry.action.toLowerCase().replaceAll('_', ' ')} {entry.entityType}
                        </span>
                      </p>
                      <p className="text-xs text-muted">{formatDateTime(entry.occurredAt, format)}</p>
                    </div>
                  ))
                )}
              </CardBody>
            </Card>
          </Can>
        </div>
      </div>
    </>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  tone = 'neutral',
  to,
}: {
  icon: React.ElementType;
  label: string;
  value: string | null;
  hint?: string;
  tone?: 'neutral' | 'brand' | 'warning' | 'danger';
  to?: string;
}) {
  const tones = {
    neutral: 'bg-sunken text-secondary',
    brand: 'bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-100',
    warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200',
    danger: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200',
  };

  const body = (
    <Card className="h-full transition-shadow hover:shadow-sm">
      <CardBody className="flex items-start gap-3">
        <span className={`grid size-10 shrink-0 place-items-center rounded-lg ${tones[tone]}`}>
          <Icon className="size-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-sm text-secondary">{label}</p>
          <p className="mt-0.5 text-2xl font-semibold tabular">
            {value ?? <span className="inline-block h-7 w-20 animate-pulse rounded bg-sunken" />}
          </p>
          {hint && <p className="mt-0.5 text-xs text-muted">{hint}</p>}
        </div>
      </CardBody>
    </Card>
  );

  return to ? <Link to={to} className="block">{body}</Link> : body;
}

function SummaryRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm text-secondary">
        <Icon className="size-4" aria-hidden />
        {label}
      </span>
      <span className="font-medium tabular">{value}</span>
    </div>
  );
}
